import {
  classifyLoadRisk
} from './recording-health.js';
import { getSupportedMimeType } from '../recording-utils.js';
import { selectRecordingRuntimeProfile } from './recording-policy.js';
import { nativeRecordingGuard } from './native-recording-guard.js';
import {
  calculateRecordingCapacity,
  chooseAvailableBytes
} from './recording-capacity.js';

const MIME_TYPES = [
  'video/mp4;codecs=avc1',
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm'
];

function selectSupportedMimeType(MediaRecorderCtor) {
  if (MediaRecorderCtor === globalThis.MediaRecorder) {
    const mimeType = getSupportedMimeType();
    const hasSupportedType = typeof MediaRecorderCtor?.isTypeSupported === 'function'
      && MIME_TYPES.some(type => MediaRecorderCtor.isTypeSupported(type));
    return { mimeType, fallback: !hasSupportedType };
  }
  if (typeof MediaRecorderCtor?.isTypeSupported !== 'function') {
    return { mimeType: 'video/webm', fallback: true };
  }
  const mimeType = MIME_TYPES.find(type => MediaRecorderCtor.isTypeSupported(type));
  return mimeType
    ? { mimeType, fallback: false }
    : { mimeType: 'video/webm', fallback: true };
}

function addOnce(items, value) {
  if (!items.includes(value)) items.push(value);
}

function createBaseReport(checkedAt) {
  return {
    status: 'ready',
    checkedAt,
    blocking: [],
    warnings: [],
    checks: {},
    trackSettings: { video: null, audio: null },
    metrics: {}
  };
}

export async function runDeviceCheck(options = {}) {
  const navigatorObject = globalThis.navigator;
  const {
    mediaDevices = navigatorObject?.mediaDevices,
    MediaRecorderCtor = globalThis.MediaRecorder,
    storageManager = navigatorObject?.storage,
    batteryProvider = navigatorObject?.getBattery?.bind(navigatorObject),
    connection = navigatorObject?.connection,
    fetchFn = globalThis.fetch?.bind(globalThis),
    settings = {},
    isNative = false,
    nativeGuard = nativeRecordingGuard,
    userAgent = navigatorObject?.userAgent || '',
    now = Date.now
  } = options;

  const report = createBaseReport(now());
  let stream = null;

  try {
    try {
      stream = await mediaDevices?.getUserMedia?.({ video: true, audio: true });
    } catch (error) {
      report.blocking.push('camera', 'microphone');
      report.checks.camera = { status: 'blocked', message: error?.message || 'Camera access failed' };
      report.checks.microphone = { status: 'blocked', message: error?.message || 'Microphone access failed' };
      report.status = 'blocked';
      return report;
    }

    if (!stream) {
      report.blocking.push('camera', 'microphone');
      report.checks.camera = { status: 'blocked', message: 'Camera is unavailable' };
      report.checks.microphone = { status: 'blocked', message: 'Microphone is unavailable' };
      report.status = 'blocked';
      return report;
    }

    const videoTrack = stream.getVideoTracks?.().find(track => track?.readyState === 'live');
    const audioTrack = stream.getAudioTracks?.().find(track => track?.readyState === 'live');

    if (!videoTrack) report.blocking.push('camera');
    if (!audioTrack) report.blocking.push('microphone');
    report.checks.camera = {
      status: videoTrack ? 'ready' : 'blocked',
      message: videoTrack ? 'Live camera track available' : 'No live camera track'
    };
    report.checks.microphone = {
      status: audioTrack ? 'ready' : 'blocked',
      message: audioTrack ? 'Live microphone track available' : 'No live microphone track'
    };
    report.trackSettings.video = videoTrack?.getSettings?.() || null;
    report.trackSettings.audio = audioTrack?.getSettings?.() || null;

    const codec = selectSupportedMimeType(MediaRecorderCtor);
    report.checks.codec = {
      status: codec.fallback ? 'warning' : 'ready',
      mimeType: codec.mimeType
    };
    report.metrics.mimeType = codec.mimeType;
    if (codec.fallback) addOnce(report.warnings, 'codec');

    const trackFrameRate = Number(report.trackSettings.video?.frameRate);
    const runtimeProfile = selectRecordingRuntimeProfile(settings, {
      isNative,
      userAgent,
      actualFps: Number.isFinite(trackFrameRate) ? trackFrameRate : undefined
    });
    report.metrics.runtimeProfile = runtimeProfile;

    let charging = false;
    let nativeCapacity = null;
    if (isNative && nativeGuard?.getCapacity) {
      nativeCapacity = await nativeGuard.getCapacity();
      Object.assign(report.metrics, {
        nativeAvailableBytes: nativeCapacity.availableBytes,
        thermalSupported: nativeCapacity.thermalSupported,
        thermalStatus: nativeCapacity.thermalStatus,
        thermalHeadroom: nativeCapacity.thermalHeadroom
      });
      report.checks.nativeCapacity = {
        status: nativeCapacity.availableBytes === null ? 'unavailable' : 'ready',
        availableBytes: nativeCapacity.availableBytes
      };
      report.checks.thermal = {
        status: nativeCapacity.thermalSupported
          ? Number(nativeCapacity.thermalStatus) >= 4 ? 'warning' : 'ready'
          : 'unavailable',
        thermalStatus: nativeCapacity.thermalStatus,
        thermalHeadroom: nativeCapacity.thermalHeadroom
      };
      if (Number(nativeCapacity.thermalStatus) >= 4) addOnce(report.warnings, 'thermal');
    }

    let browserEstimate = null;
    if (storageManager?.estimate) {
      try {
        browserEstimate = await storageManager.estimate();
        const selectedCapacity = chooseAvailableBytes({
          nativeAvailableBytes: nativeCapacity?.availableBytes,
          browserQuota: browserEstimate?.quota,
          browserUsage: browserEstimate?.usage
        });
        const capacity = calculateRecordingCapacity({
          availableBytes: selectedCapacity.availableBytes,
          videoBitrate: runtimeProfile.videoBitrate,
          audioBitrate: runtimeProfile.audioBitrate,
          targetMinutes: Number(settings.recordingTargetMinutes) || 120
        });
        const estimatedMinutes = capacity.remainingMinutes;
        Object.assign(report.metrics, {
          storageQuota: browserEstimate?.quota ?? null,
          storageUsage: browserEstimate?.usage ?? null,
          estimatedMinutes,
          storageSource: selectedCapacity.source,
          storageApproximate: selectedCapacity.approximate,
          availableBytes: selectedCapacity.availableBytes,
          requiredBytes: capacity.requiredBytes
        });
        const insufficient = capacity.sufficient === false;
        const lowStorage = insufficient || (estimatedMinutes !== null && estimatedMinutes < 30);
        report.checks.storage = {
          status: insufficient ? 'blocked' : lowStorage ? 'warning' : 'ready',
          estimatedMinutes,
          availableBytes: selectedCapacity.availableBytes,
          requiredBytes: capacity.requiredBytes,
          source: selectedCapacity.source,
          approximate: selectedCapacity.approximate,
          message: insufficient
            ? `Not enough protected space for ${Number(settings.recordingTargetMinutes) || 120} minutes`
            : selectedCapacity.approximate
              ? 'Browser storage estimate (approximate)'
              : 'Device storage checked'
        };
        if (lowStorage) addOnce(report.warnings, 'storage');
        if (insufficient) addOnce(report.blocking, 'storage');
      } catch (error) {
        report.checks.storage = { status: 'unavailable', estimatedMinutes: null, message: error?.message || 'Storage estimate unavailable' };
      }
    } else if (nativeCapacity?.availableBytes !== null && nativeCapacity?.availableBytes !== undefined) {
      const capacity = calculateRecordingCapacity({
        availableBytes: nativeCapacity.availableBytes,
        videoBitrate: runtimeProfile.videoBitrate,
        audioBitrate: runtimeProfile.audioBitrate,
        targetMinutes: Number(settings.recordingTargetMinutes) || 120
      });
      Object.assign(report.metrics, {
        estimatedMinutes: capacity.remainingMinutes,
        storageSource: 'native',
        storageApproximate: false,
        availableBytes: nativeCapacity.availableBytes,
        requiredBytes: capacity.requiredBytes
      });
      const insufficient = capacity.sufficient === false;
      report.checks.storage = {
        status: insufficient ? 'blocked' : 'ready',
        estimatedMinutes: capacity.remainingMinutes,
        availableBytes: nativeCapacity.availableBytes,
        requiredBytes: capacity.requiredBytes,
        source: 'native',
        approximate: false
      };
      if (insufficient) {
        addOnce(report.warnings, 'storage');
        addOnce(report.blocking, 'storage');
      }
    } else {
      report.checks.storage = { status: 'unavailable', estimatedMinutes: null };
    }

    if (typeof batteryProvider === 'function') {
      try {
        const battery = await batteryProvider();
        charging = !!battery?.charging;
        report.metrics.batteryLevel = Number.isFinite(battery?.level) ? battery.level : null;
        report.metrics.charging = charging;
        const lowBattery = Number.isFinite(battery?.level) && battery.level <= 0.2 && !charging;
        report.checks.battery = { status: lowBattery ? 'warning' : 'ready', level: report.metrics.batteryLevel, charging };
        if (lowBattery) addOnce(report.warnings, 'battery');
      } catch (error) {
        report.checks.battery = { status: 'unavailable', message: error?.message || 'Battery status unavailable' };
      }
    } else {
      report.checks.battery = { status: 'unavailable' };
    }

    const slowConnection = connection?.saveData === true
      || connection?.effectiveType === 'slow-2g'
      || connection?.effectiveType === '2g'
      || (Number.isFinite(connection?.downlink) && connection.downlink <= 0.5)
      || (Number.isFinite(connection?.rtt) && connection.rtt >= 500);
    if (connection) {
      Object.assign(report.metrics, {
        downlinkMbps: connection.downlink ?? null,
        effectiveType: connection.effectiveType ?? null,
        networkRttMs: connection.rtt ?? null,
        saveData: !!connection.saveData
      });
      report.checks.network = { status: slowConnection ? 'warning' : 'ready' };
      if (slowConnection) addOnce(report.warnings, 'network');
    } else {
      report.checks.network = { status: 'unavailable' };
    }

    if (typeof fetchFn === 'function') {
      try {
        const startedAt = now();
        const response = await fetchFn('/', { method: 'HEAD', cache: 'no-store' });
        const latencyMs = Math.max(0, now() - startedAt);
        report.metrics.sameOriginLatencyMs = latencyMs;
        const slowLatency = response?.ok === false || latencyMs >= 1_000;
        report.checks.latency = { status: slowLatency ? 'warning' : 'ready', latencyMs };
        if (slowLatency) addOnce(report.warnings, 'network');
      } catch (error) {
        report.checks.latency = { status: 'warning', latencyMs: null, message: error?.message || 'Same-origin request failed' };
        addOnce(report.warnings, 'network');
      }
    } else {
      report.checks.latency = { status: 'unavailable', latencyMs: null };
    }

    const loadRisk = classifyLoadRisk({
      resolution: runtimeProfile.resolution,
      fps: runtimeProfile.fps,
      charging,
      actualFps: Number.isFinite(trackFrameRate) ? trackFrameRate : undefined
    });
    report.metrics.loadRisk = loadRisk;
    report.checks.load = { status: loadRisk === 'healthy' ? 'ready' : 'warning', risk: loadRisk };
    if (loadRisk !== 'healthy') addOnce(report.warnings, 'load');

    report.status = report.blocking.length ? 'blocked' : report.warnings.length ? 'warning' : 'ready';
    return report;
  } finally {
    for (const track of stream?.getTracks?.() || []) {
      try { track.stop?.(); } catch (_) {}
    }
  }
}

export function isDeviceCheckFresh(report, now = Date.now(), maxAgeMs = 1_800_000) {
  if (!Number.isFinite(report?.checkedAt) || !Number.isFinite(now) || !Number.isFinite(maxAgeMs) || maxAgeMs < 0) {
    return false;
  }
  const age = now - report.checkedAt;
  return age >= 0 && age <= maxAgeMs;
}
